import { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import {
  Phone, Building2, RefreshCw, DollarSign, Clock, Edit2, Check, X,
  AlertTriangle, TrendingUp, ChevronDown, ChevronRight, User
} from 'lucide-react';

function fmtCost(n) { return n ? `$${Number(n).toFixed(2)}` : '$0.00'; }
function fmtMins(s) { return s ? `${Math.ceil(s / 60)}m` : '0m'; }
function fmtNum(n)  { return n ? Number(n).toLocaleString() : '0'; }

// Dual-band quota bar — shows soft threshold marker and hard limit
function QuotaBar({ used, soft, hard, label }) {
  if (!hard && !soft) return <span className="text-xs text-gray-400">Unlimited</span>;
  const limit = hard || soft;
  const pct     = Math.min(100, Math.round((used / limit) * 100));
  const softPct = (soft && hard) ? Math.min(100, Math.round((soft / hard) * 100)) : null;
  const color   = pct >= 100 ? 'bg-red-500' : pct >= (softPct ?? 80) ? 'bg-amber-400' : 'bg-green-500';
  return (
    <div className="space-y-0.5 min-w-[90px]">
      <div className="flex justify-between text-[10px] text-gray-500">
        <span>{used}</span>
        <span>{hard ?? '∞'}{soft && hard ? <span className="text-gray-300"> / {soft}⚠</span> : ''}</span>
      </div>
      <div className="relative h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
        {softPct && <div className="absolute top-0 h-full w-px bg-amber-400 opacity-70" style={{ left: `${softPct}%` }} />}
      </div>
      <p className="text-[10px] text-gray-400 text-right">{pct}% {label}</p>
    </div>
  );
}

// Quota field pair: soft + hard
function QuotaFieldPair({ label, soft, hard, onSoftChange, onHardChange }) {
  return (
    <div className="space-y-0.5">
      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <div className="flex gap-1.5">
        <div>
          <label className="block text-[9px] text-amber-600 mb-0.5">Soft ⚠</label>
          <input type="number" min="1" value={soft} onChange={e => onSoftChange(e.target.value)}
            placeholder="∞" className="w-16 px-1.5 py-1 text-xs border border-amber-300 rounded focus:ring-1 focus:ring-amber-400" />
        </div>
        <div>
          <label className="block text-[9px] text-red-600 mb-0.5">Hard ✋</label>
          <input type="number" min="1" value={hard} onChange={e => onHardChange(e.target.value)}
            placeholder="∞" className="w-16 px-1.5 py-1 text-xs border border-red-300 rounded focus:ring-1 focus:ring-red-400" />
        </div>
      </div>
    </div>
  );
}

function CompanyQuotaEditor({ company, onSaved }) {
  const [callsSoft,  setCallsSoft]  = useState(company.CallsSoft  ?? '');
  const [callsHard,  setCallsHard]  = useState(company.CallsHard  ?? '');
  const [minsSoft,   setMinsSoft]   = useState(company.MinutesSoft ?? '');
  const [minsHard,   setMinsHard]   = useState(company.MinutesHard ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState(null);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await apiClient.patch(`/platform/voice-usage/${company.CompanyID}/quota`, {
        callsHard:   callsHard  === '' ? null : Number(callsHard),
        callsSoft:   callsSoft  === '' ? null : Number(callsSoft),
        minutesHard: minsHard   === '' ? null : Number(minsHard),
        minutesSoft: minsSoft   === '' ? null : Number(minsSoft),
      });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-wrap items-end gap-3 bg-gray-50 rounded-lg p-3">
      <QuotaFieldPair label="Calls/mo" soft={callsSoft} hard={callsHard} onSoftChange={setCallsSoft} onHardChange={setCallsHard} />
      <QuotaFieldPair label="Mins/mo"  soft={minsSoft}  hard={minsHard}  onSoftChange={setMinsSoft}  onHardChange={setMinsHard}  />
      <div className="flex gap-1.5 items-center">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50">
          <Check className="w-3 h-3" />{saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onSaved} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
      </div>
      {err && <p className="text-xs text-red-500 w-full mt-1">{err}</p>}
    </div>
  );
}

function UserQuotaEditor({ companyId, user, onSaved }) {
  const [callsSoft,  setCallsSoft]  = useState(user.VoiceQuotaCallsPerMonthSoft   ?? '');
  const [callsHard,  setCallsHard]  = useState(user.VoiceQuotaCallsPerMonthHard   ?? '');
  const [minsSoft,   setMinsSoft]   = useState(user.VoiceQuotaMinutesPerMonthSoft  ?? '');
  const [minsHard,   setMinsHard]   = useState(user.VoiceQuotaMinutesPerMonthHard  ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState(null);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await apiClient.patch(`/platform/voice-usage/${companyId}/users/${user.UserID}/quota`, {
        callsHard:   callsHard  === '' ? null : Number(callsHard),
        callsSoft:   callsSoft  === '' ? null : Number(callsSoft),
        minutesHard: minsHard   === '' ? null : Number(minsHard),
        minutesSoft: minsSoft   === '' ? null : Number(minsSoft),
      });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <QuotaFieldPair label="Calls" soft={callsSoft} hard={callsHard} onSoftChange={setCallsSoft} onHardChange={setCallsHard} />
      <QuotaFieldPair label="Mins"  soft={minsSoft}  hard={minsHard}  onSoftChange={setMinsSoft}  onHardChange={setMinsHard}  />
      <div className="flex gap-1.5 items-center">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50">
          <Check className="w-3 h-3" />{saving ? '…' : 'Save'}
        </button>
        <button onClick={() => onSaved(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
      </div>
      {err && <p className="text-xs text-red-500 w-full mt-1">{err}</p>}
    </div>
  );
}

function UserRow({ companyId, user, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const usedMins = Math.ceil(user.SecondsThisMonth / 60);
  return (
    <tr className="border-t border-gray-100 bg-gray-50/50">
      <td className="pl-10 pr-3 py-2">
        <div className="flex items-center gap-1.5">
          <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700">{user.UserName}</p>
            <p className="text-[10px] text-gray-400">{user.Email}</p>
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-center">
        <QuotaBar used={user.CallsThisMonth} soft={user.VoiceQuotaCallsPerMonthSoft} hard={user.VoiceQuotaCallsPerMonthHard} label="calls" />
      </td>
      <td className="px-3 py-2 text-center">
        <QuotaBar used={usedMins} soft={user.VoiceQuotaMinutesPerMonthSoft} hard={user.VoiceQuotaMinutesPerMonthHard} label="mins" />
      </td>
      <td className="px-3 py-2" colSpan={5}>
        {editing ? (
          <UserQuotaEditor companyId={companyId} user={user} onSaved={(reload = true) => { setEditing(false); if (reload) onRefresh(); }} />
        ) : (
          <button onClick={() => setEditing(true)}
            className="flex items-center gap-1 px-2 py-1 text-[10px] text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
            <Edit2 className="w-3 h-3" />Set user quota
          </button>
        )}
      </td>
    </tr>
  );
}

function CompanyRow({ company, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers]       = useState(null);
  const [editing, setEditing]   = useState(false);
  const usedMins = Math.ceil(company.SecondsThisMonth / 60);

  const loadUsers = useCallback(() => {
    apiClient.get(`/platform/voice-usage/${company.CompanyID}/users`)
      .then(r => setUsers(r.data))
      .catch(() => setUsers([]));
  }, [company.CompanyID]);

  const toggle = () => {
    if (!expanded && !users) loadUsers();
    setExpanded(e => !e);
  };

  return (
    <>
      <tr className={`hover:bg-gray-50 ${company.CallsThisMonth === 0 ? 'opacity-60' : ''}`}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <button onClick={toggle} className="text-gray-400 hover:text-gray-600 shrink-0">
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="font-medium text-gray-900 truncate max-w-[160px]">{company.CompanyName}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-center">
          <QuotaBar used={company.CallsThisMonth} soft={company.CallsSoft} hard={company.CallsHard} label="calls" />
        </td>
        <td className="px-4 py-3 text-center">
          <QuotaBar used={usedMins} soft={company.MinutesSoft} hard={company.MinutesHard} label="mins" />
        </td>
        <td className="px-4 py-3 text-center text-xs text-gray-600">
          {fmtNum(company.InputTokens)} / {fmtNum(company.OutputTokens)}
        </td>
        <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-gray-900">
          {fmtCost(company.TotalCostUSD)}
        </td>
        <td className="px-4 py-3 text-center text-xs">
          <span className="text-amber-600">{company.CallsSoft ?? '—'}</span>
          <span className="text-gray-300 mx-1">/</span>
          <span className="text-red-600">{company.CallsHard ?? '—'}</span>
        </td>
        <td className="px-4 py-3 text-center text-xs">
          <span className="text-amber-600">{company.MinutesSoft ?? '—'}</span>
          <span className="text-gray-300 mx-1">/</span>
          <span className="text-red-600">{company.MinutesHard ?? '—'}</span>
        </td>
        <td className="px-4 py-3">
          {editing ? (
            <CompanyQuotaEditor company={company} onSaved={() => { setEditing(false); onRefresh(); }} />
          ) : (
            <button onClick={() => setEditing(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
              <Edit2 className="w-3 h-3" />Set quota
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        users === null
          ? <tr><td colSpan={8} className="pl-10 py-2 text-xs text-gray-400">Loading users…</td></tr>
          : users.length === 0
            ? <tr><td colSpan={8} className="pl-10 py-2 text-xs text-gray-400">No users found.</td></tr>
            : users.map(u => (
                <UserRow key={u.UserID} companyId={company.CompanyID} user={u} onRefresh={loadUsers} />
              ))
      )}
    </>
  );
}


export default function PlatformVoiceUsage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get('/platform/voice-usage')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const companies = data?.companies || [];
  const totals    = data?.totals    || {};
  const month     = new Date().toLocaleString('en-GB', { month: 'long', year: 'numeric' });

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-100 rounded-xl">
            <Phone className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Voice Agent Usage & Quotas</h1>
            <p className="text-sm text-gray-500">Platform-wide AI voice call usage — {month}</p>
          </div>
        </div>
        <button onClick={load} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Platform totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Phone,      label: 'Total Calls',      value: fmtNum(totals.TotalCalls),   color: 'text-blue-700 bg-blue-50' },
          { icon: Clock,      label: 'Total Minutes',    value: fmtMins(totals.TotalSeconds), color: 'text-purple-700 bg-purple-50' },
          { icon: DollarSign, label: 'Estimated Cost',   value: fmtCost(totals.TotalCostUSD), color: 'text-red-700 bg-red-50' },
          { icon: TrendingUp, label: 'Active Companies', value: companies.filter(c => c.CallsThisMonth > 0).length, color: 'text-green-700 bg-green-50' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Quotas are per-user and per-company. <strong>Soft ⚠</strong> = warning banner shown to the user (call still allowed).{' '}
          <strong>Hard ✋</strong> = call blocked (429). Sum of user hard quotas cannot exceed the company hard quota. Leave blank for unlimited.
          Cost estimate: GPT-4o $2.50/$10.00 per 1M tokens + ACS $0.028/min.
        </span>
      </div>

      {/* Per-company table */}
      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="w-6 h-6 text-gray-400 animate-spin" /></div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Company / User</th>
                <th className="text-center px-4 py-3">Calls used</th>
                <th className="text-center px-4 py-3">Minutes used</th>
                <th className="text-center px-4 py-3">Tokens in/out</th>
                <th className="text-right px-4 py-3">Est. Cost</th>
                <th className="text-center px-4 py-3">Call quota ⚠/✋</th>
                <th className="text-center px-4 py-3">Min quota ⚠/✋</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {companies.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400 text-sm">No voice calls recorded this month</td></tr>
              )}
              {companies.map(c => (
                <CompanyRow key={c.CompanyID} company={c} onRefresh={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
